import {
  useState,
  useEffect,
  Fragment,
  isValidElement,
  cloneElement,
} from 'react';
import { useStateWithCallbackInstant } from 'use-state-with-callback';
import { makeStyles } from 'tss-react/mui';
import { Button, Table, Toolbar } from '@mui/material';
import { formatCurrency, getReceiverName } from '../../lib';
import {
  useDispatch,
  getServicesAssigneesPerActivity,
  useSelector,
  uiSelectors,
  incrementViewVersion,
} from '../../state';
import {
  DataGrid,
  TextField,
  FunctionField,
  AssignUserToServiceForm,
  AdjustEmployeeTrackingForm,
  ConfirmStatusIcons,
  ListEditIcon,
  CurrencyField,
  DateField,
} from '..';
import { EmployeeWorkTotals } from './EmployeeWorkTotals';
import { ActivitySectionCard } from './ActivitySectionCard';
import { useGetUsersData } from '../../hooks';

/**
 *
 * @param {object} param
 * @param {string | number} param.entityId
 * @param {string | number} [param.warehouseId]
 * @param {import('../../types').SystemServiceType} param.serviceType
 * @param {boolean} [param.disableAssigning]
 * @param {Array<import('../../types').FulfilledServiceAssigneeTaskType>} [param.taskTypesListFilter]
 * @param {import('../../types').FulfilledServiceAssigneeTaskType} [param.taskTypeToAdd]
 * @param {string} [param.title]
 * @param {Record<string, any>} [param.fieldsToHide]
 * @param {string} [param.addText]
 * @param {string} [param.timezoneId]
 * @param {import('react').ReactElement} [param.emptyTextComponent]
 * @param {Record<string, any>} [param.defaultData]
 * @param {number} [param.customViewVersion]
 * @param {(hasAssignees: any) => any} [param.getHasAssignees]
 * @param {boolean} [param.hasCustomAdd]
 * @param {(editData: any) => any} [param.handleSetEditData]
 * @param {number} [param.rowsPerPage]
 * @param {Array<number>} [param.rowsPerPageOptions]
 * @param {boolean} [param.hideTasks]
 * @param {boolean} [param.showWorkSummary]
 * @param {import('react').ReactNode} [param.children]
 * @returns
 */
export const ServiceAssignees = ({
  entityId,
  serviceType,
  disableAssigning,
  warehouseId,
  timezoneId,
  taskTypesListFilter,
  taskTypeToAdd = 'SpecialService',
  title = 'Manual tasks',
  fieldsToHide,
  addText = 'Add task',
  emptyTextComponent,
  defaultData,
  customViewVersion,
  getHasAssignees,
  hasCustomAdd,
  handleSetEditData,
  rowsPerPage,
  rowsPerPageOptions,
  showWorkSummary = false,
  hideTasks = false,
  children,
}) => {
  const dispatch = useDispatch();
  const globalViewVersion = useSelector(uiSelectors.viewVersion);
  const [workSummary, setWorkSummary] = useState();
  const { classes, css } = useStyles();
  const { isAdmin, isCustomerService, isCustomer } = useGetUsersData();
  const [assigneesData, setAssigneesData] = useState([]);
  const [tasksTotalTime, setTasksTotalTime] = useState('0 sec');
  const [tasksTotalExpense, setTasksTotalExpense] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [hasAssignees, setHasAssignees] = useState();
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useStateWithCallbackInstant(null, (data) =>
    setShowEdit(!!data),
  );
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustData, setAdjustData] = useStateWithCallbackInstant(
    null,
    (data) => setShowAdjustModal(!!data),
  );

  useEffect(() => {
    let active = true;
    setWorkSummary(undefined);
    (async function () {
      const { data } = await dispatch(
        getServicesAssigneesPerActivity({
          entityId,
          serviceType,
          warehouseId,
          taskTypes: taskTypesListFilter ?? ['SpecialService'],
        }),
      );
      if (data && active) {
        setWorkSummary(data.employeeWorkSummary);
        const mapped = data.serviceActivities.reduce((acc, cur) => {
          const { assigneeActivities, ...rest } = cur;
          const rv = { ...rest, assigneeActivities };
          const ids = [];
          const rows = assigneeActivities.reduce((accA, curA) => {
            const id =
              curA.userID + (curA.trackingID ?? 0) + (curA.taskType ?? '') + '';
            accA[id] = curA;
            ids.push(id);
            return accA;
          }, {});
          rv.assigneeIds = ids;
          rv.assigneeRows = rows;
          acc.push(rv);
          return acc;
        }, []);
        setAssigneesData(mapped);
        setTasksTotalTime(data.totalDurationDisplay);
        setTasksTotalExpense(data.totalExpense ?? 0);
        setHasAssignees(!!mapped.length);
        typeof getHasAssignees === 'function' &&
          getHasAssignees(!!mapped.length);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityId,
    serviceType,
    warehouseId,
    customViewVersion,
    globalViewVersion,
  ]);

  if (hideTasks) {
    return (
      <>
        {!isCustomer && (
          <EmployeeWorkTotals summary={workSummary} serviceType={serviceType} />
        )}
        {children}
      </>
    );
  }

  if (hasAssignees === false && isValidElement(emptyTextComponent)) {
    return cloneElement(emptyTextComponent);
  }

  return (
    <div className={css({ marginTop: 24 })}>
      {showWorkSummary && !isCustomer && (
        <EmployeeWorkTotals summary={workSummary} serviceType={serviceType} />
      )}
      {children}
      <ActivitySectionCard title={title}>
        <Toolbar
          className={classes.actions}
          disableGutters
          sx={{ minHeight: 'unset !important' }}
        >
          <div className={classes.tasksTotals}>
            <span>{`Total time spent ${tasksTotalTime}`}</span>
            {isAdmin && (
              <Fragment>
                <span className={classes.spacer}>|</span>
                <span>{`Total expense ${formatCurrency(
                  tasksTotalExpense,
                )}`}</span>
              </Fragment>
            )}
          </div>
          {(isAdmin || isCustomerService) &&
            !disableAssigning &&
            !hasCustomAdd && (
              <Button
                variant='contained'
                size='large'
                disableElevation
                onClick={() => setShowAdd(true)}
              >
                {addText}
              </Button>
            )}
        </Toolbar>
        {showAdd && (
          <AssignUserToServiceForm
            open={showAdd}
            handleClose={() => setShowAdd(false)}
            setViewVersion={() => dispatch(incrementViewVersion())}
            entityId={entityId}
            warehouseId={warehouseId}
            serviceType={serviceType}
            fieldsToHide={fieldsToHide}
            title={title}
            taskType={taskTypeToAdd}
            currentData={defaultData}
          />
        )}
        {showEdit && (
          <AssignUserToServiceForm
            open={showEdit}
            handleClose={() => setEditData(null)}
            setViewVersion={() => dispatch(incrementViewVersion())}
            entityId={entityId}
            serviceType={serviceType}
            currentData={{ ...(defaultData ?? {}), ...editData }}
            warehouseId={warehouseId}
            fieldsToHide={fieldsToHide}
            title={title}
            taskType={taskTypeToAdd}
          />
        )}
        {showAdjustModal && (
          <AdjustEmployeeTrackingForm
            adjustActivityFormType='Assignee'
            open={showAdjustModal}
            handleClose={() => setAdjustData(null)}
            setViewVersion={() => dispatch(incrementViewVersion())}
            startTime={adjustData?.startTime}
            endTime={adjustData?.endTime}
            isCompleted={adjustData?.isCompleted}
            trackingId={adjustData?.trackingID}
            assigneesId={adjustData?.assigneesID}
          />
        )}
        {assigneesData.map((d, i) => {
          const {
            nickname,
            isCompleted,
            assigneeIds,
            assigneeRows,
            totalDurationDisplay,
            totalExpense,
          } = d;

          return (
            <div
              key={i}
              className={css({
                marginBottom: i < assigneesData.length - 1 ? 40 : 0,
              })}
            >
              <div
                className={css({ display: 'flex', flexDirection: 'column' })}
              >
                <span className={classes.subTitle}>{nickname}</span>
                <div className={classes.details}>
                  <ConfirmStatusIcons
                    isConfirmed={isCompleted}
                    confirmedText='Completed'
                    unconfirmedText='Not completed'
                  />
                  <span className={classes.spacer}>|</span>
                  {`Total time spent ${totalDurationDisplay}`}
                  {isAdmin && (
                    <Fragment>
                      <span className={classes.spacer}>|</span>
                      <span>{`Total expense ${formatCurrency(
                        totalExpense,
                      )}`}</span>
                    </Fragment>
                  )}
                  {(isAdmin || isCustomerService) &&
                    !disableAssigning &&
                    !isCompleted && (
                      <ListEditIcon
                        onClick={() =>
                          handleSetEditData
                            ? handleSetEditData(d)
                            : setEditData(d)
                        }
                        className={css({ marginLeft: 5 })}
                      />
                    )}
                </div>
              </div>
              <Table>
                <DataGrid
                  bulkActionButtons={false}
                  ids={assigneeIds}
                  rows={assigneeRows}
                  hideFooter
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={rowsPerPageOptions}
                >
                  <FunctionField
                    source='userFirstName'
                    label='User'
                    sortable={false}
                    render={(record) => {
                      const { userFirstName, userLastName } = record;
                      return getReceiverName({
                        firstName: userFirstName,
                        lastName: userLastName,
                      });
                    }}
                  />
                  <TextField
                    source='userTypeDisplay'
                    headerClassName={classes.noWrap}
                    label='User type'
                    sortable={false}
                  />
                  <TextField
                    source='taskTypeDisplay'
                    headerClassName={classes.noWrap}
                    label='Task'
                    sortable={false}
                  />
                  <FunctionField
                    source='userFirstName'
                    label='vehicles'
                    sortable={false}
                    render={(record) => {
                      const { assigneeVehicleTypes } = record;
                      if (!assigneeVehicleTypes?.length) {
                        return '--';
                      }
                      return assigneeVehicleTypes
                        .map((a) => a.vehicleTypeDisplay)
                        .join(', ');
                    }}
                  />
                  <DateField
                    source='startTime'
                    label='Start'
                    sortable={false}
                    format='Pp zzz'
                    useTime
                    timeZone={timezoneId}
                    headerClassName={classes.noWrap}
                  />
                  <DateField
                    source='endTime'
                    label='End'
                    sortable={false}
                    format='Pp zzz'
                    timeZone={timezoneId}
                    useTime
                    headerClassName={classes.noWrap}
                  />
                  <TextField
                    source='durationDisplay'
                    headerClassName={classes.noWrap}
                    label='time spent'
                    sortable={false}
                  />
                  {isAdmin && (
                    <CurrencyField
                      source='expense'
                      label='expense'
                      sortable={false}
                    />
                  )}
                  {(isAdmin || isCustomerService) && !disableAssigning && (
                    <FunctionField
                      source=''
                      label=''
                      sortable={false}
                      render={(record) => {
                        const {
                          trackingID,
                          assigneesID,
                          startTime,
                          endTime,
                          isCompleted,
                        } = record;

                        return trackingID ? (
                          <ListEditIcon
                            titleAccess='Edit employee activity time'
                            onClick={() =>
                              setAdjustData({
                                startTime,
                                endTime,
                                isCompleted,
                                trackingID,
                                assigneesID,
                              })
                            }
                          />
                        ) : null;
                      }}
                    />
                  )}
                </DataGrid>
              </Table>
            </div>
          );
        })}
      </ActivitySectionCard>
    </div>
  );
};

const useStyles = makeStyles({ name: { ServiceAssignees } })((theme) => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tasksTotals: {
    fontFamily: 'Montserrat',
    fontSize: 11.5,
    display: 'flex',
    alignItems: 'center',
  },
  sectionTitle: {
    fontFamily: 'Montserrat',
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: 0,
    marginBottom: 24,
  },
  subTitle: {
    fontFamily: 'Montserrat',
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: 0,
    marginBottom: 8,
  },
  details: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    letterSpacing: 0,
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  spacer: {
    margin: '0px 16px',
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
}));
